import React, { useCallback, useEffect, useRef } from 'react';
import { View } from 'react-native';
import { useGoodsRelatedOutfit } from '../queries/useGoodsRelatedOutfit';
import {
  Colors,
  Divider,
  Flex,
  Headline,
  HorizontalSpacing,
  IconBookmark,
  IconBookmarkFilled,
  IconHeart,
  IconHeartFill,
  IconTalkRegular,
  List,
  RemoteImage,
  Typography,
  VerticalSpacing,
} from '@banhala/ui-kit';
import { bridge, formatNumber, isWeb, localizeNumber } from 'shared/src/utils';
import type { FlatListProps } from 'react-native';
import type { RelatedOutfitItem } from 'shared/src/api/goodsService/getRelatedOutfits';
import { useLikeOutfit } from '../queries/useLikeOutfit';
import { useBookmarkOutfit } from '../queries/useBookmarkOutfit';
import {
  useAppDownloadModal,
  useIsAnonymousUser,
  useOpenDeeplink,
  useOutfitBookmarkMap,
} from 'shared/src/hooks';
import {
  useElementViewportTracking,
  useIsSectionVisible,
} from '../../../../../store/sectionVisibility';
import { useLogGoodsAnalyticsEvent } from '../../../../../hooks/useLogGoodsAnalyticsEvent';

type RelatedOutfitsProps = {
  focused: boolean;
  onChangeHeight: (height: number) => void;
};

const RelatedOutfitsContent: React.FC<RelatedOutfitsProps> = props => {
  const { data: relatedOutfits } = useGoodsRelatedOutfit();
  const isAnonymouseUser = useIsAnonymousUser();
  const contentLoggedList = useRef<Array<number>>([]);
  const pendingContentItems = useRef<
    Array<{ index: number; item: RelatedOutfitItem }>
  >([]);
  const trackingRef = useElementViewportTracking('related-outfits', 360);
  const isVisible = useIsSectionVisible('related-outfits');
  const isVisibleRef = useRef(isVisible);
  isVisibleRef.current = isVisible;

  const { mutateAsync: likeOutfit } = useLikeOutfit();
  const { mutateAsync: bookmarkOutfit } = useBookmarkOutfit();
  const { show: showAppDownloadModal } = useAppDownloadModal();
  const { openDeeplink } = useOpenDeeplink();
  const { bookmarkMap, setBookmarkMap } = useOutfitBookmarkMap({
    initialMap: relatedOutfits.item_list.reduce(
      (acc, cur) => ({ ...acc, [cur.item.sno]: cur.item.bookmark.bookmarked }),
      {},
    ),
  });

  useEffect(() => {
    if (isVisible && props.focused && pendingContentItems.current.length > 0) {
      pendingContentItems.current.forEach(({ index, item }) => {
        if (contentLoggedList.current.includes(index)) return;
        contentLoggedList.current.push(index);

        bridge.logAnalyticsEvent({
          name: 'DISPLAY_CONTENT',
          channel: ['ablylog'],
          analytics: {
            SCREEN: 'GOODS_DETAIL',
            ...item.logging.analytics,
            INDEX: index,
          },
        });
      });
      pendingContentItems.current = [];
    }
  }, [isVisible, props.focused]);

  const logDisplayContent = useCallback<
    NonNullable<FlatListProps<RelatedOutfitItem>['onViewableItemsChanged']>
  >(
    ({ viewableItems }) => {
      if (!props.focused) {
        return;
      }
      viewableItems.forEach(({ index, isViewable, item }) => {
        if (index === null || !isViewable) {
          return;
        }

        if (contentLoggedList.current.includes(index)) {
          return;
        }

        if (!isVisibleRef.current) {
          if (!pendingContentItems.current.some(p => p.index === index)) {
            pendingContentItems.current.push({ index, item });
          }
          return;
        }

        contentLoggedList.current.push(index);

        bridge.logAnalyticsEvent({
          name: 'DISPLAY_CONTENT',
          analytics: {
            SCREEN: 'GOODS_DETAIL',
            ...item.logging.analytics,
            INDEX: index,
          },
        });
      });
    },
    [props.focused],
  );

  const logGoodsAnalytics = useLogGoodsAnalyticsEvent();

  const handlePressLike = useCallback(
    async (item: RelatedOutfitItem) => {
      if (isWeb) {
        showAppDownloadModal();
        return;
      }
      if (isAnonymouseUser) {
        bridge.assertLogin({});
        return;
      }
      await likeOutfit({
        sno: item.item.sno,
        liked: item.item.like.liked,
      });
      logGoodsAnalytics({
        name: 'CLICK_LIKE_BUTTON',
        analytics: {
          ...item.logging,
        },
      });
    },
    [likeOutfit, logGoodsAnalytics, isAnonymouseUser, showAppDownloadModal],
  );

  const handlePressComment = useCallback(
    (item: RelatedOutfitItem) => {
      if (isWeb) {
        showAppDownloadModal();
        return;
      }
      openDeeplink(item.item.deeplink);
    },
    [showAppDownloadModal, openDeeplink],
  );

  const handlePressBookmark = useCallback(
    async (item: RelatedOutfitItem) => {
      if (isWeb) {
        showAppDownloadModal();
        return;
      }
      if (isAnonymouseUser) {
        bridge.assertLogin({});
        return;
      }
      await bookmarkOutfit({
        sno: item.item.sno,
        bookmarked: item.item.bookmark.bookmarked,
      });
      bridge.bookmarkOutfit({
        outfit_sno: item.item.sno,
        is_bookmarked: !item.item.bookmark.bookmarked,
      });
      setBookmarkMap(prev => ({
        ...prev,
        [item.item.sno]: !item.item.bookmark.bookmarked,
      }));
    },
    [bookmarkOutfit, isAnonymouseUser, setBookmarkMap, showAppDownloadModal],
  );

  const handlePressCodiItem = useCallback(
    (item: RelatedOutfitItem, index: number) => {
      bridge.logAnalyticsEvent({
        channel: ['ablylog'],
        name: 'CLICK_CONTENT',
        analytics: {
          ...item.logging.analytics,
          SCREEN: 'GOODS_DETAIL',
          INDEX: index,
        },
      });
      if (isWeb) {
        showAppDownloadModal();
        return;
      }
      openDeeplink(item.item.deeplink);
    },
    [showAppDownloadModal, openDeeplink],
  );

  if (relatedOutfits.item_list.length === 0) return null;

  return (
    <View
      ref={trackingRef}
      onLayout={({ nativeEvent }) => {
        props.onChangeHeight(nativeEvent.layout.height);
      }}
    >
      <Divider size={'small'} />
      <Headline headline={'비슷한 상품의 코디에요'} size={'medium'} />
      <List
        data={relatedOutfits.item_list}
        horizontal
        contentContainerStyle={{ paddingHorizontal: 16 }}
        showsHorizontalScrollIndicator={false}
        ItemSeparatorComponent={() => <HorizontalSpacing size={8} />}
        renderItem={({ item, index }) => {
          const likeCount = formatNumber(item.item.like.count);
          const bookmarkCount = formatNumber(item.item.bookmark.count);
          return (
            <Flex
              gap={8}
              onPress={() => {
                handlePressCodiItem(item, index);
              }}
            >
              <Flex width={160} height={200}>
                <RemoteImage
                  uri={item.item.thumbnail}
                  width={160}
                  height={200}
                  borderRadius={'small'}
                />
                <Flex
                  position={'absolute'}
                  right={8}
                  top={8}
                  padding={{ horizontal: 6, vertical: 4 }}
                  backgroundColorValue={Colors.overlay_dark}
                  borderRadius={'small'}
                >
                  <Typography type={'subtitle5'} color={'content_always_white'}>
                    태그된 상품 {localizeNumber(item.item.tagged_goods_count)}
                  </Typography>
                </Flex>
              </Flex>

              <Flex>
                <Flex.Row
                  alignItems={'center'}
                  justifyContent={'space-between'}
                >
                  <Flex.Row gap={8}>
                    <Flex.Row gap={2} onPress={() => handlePressLike(item)}>
                      {item.item.like.liked ? (
                        <IconHeartFill
                          fill={Colors.content_pink_primary}
                          width={16}
                          height={16}
                        />
                      ) : (
                        <IconHeart fill={Colors.content_tertiary} size={16} />
                      )}
                      <Typography type={'subtitle4'} color={'content_tertiary'}>
                        {likeCount.value}
                        {likeCount.label}
                      </Typography>
                    </Flex.Row>

                    <Flex.Row gap={2} onPress={() => handlePressComment(item)}>
                      <IconTalkRegular
                        fill={Colors.content_tertiary}
                        size={16}
                      />
                      <Typography
                        type={'subtitle4'}
                        color={'content_tertiary'}
                        numberOfLines={1}
                        ellipsizeMode={'tail'}
                      >
                        댓글쓰기
                      </Typography>
                    </Flex.Row>
                  </Flex.Row>
                  <Flex.Row gap={2} onPress={() => handlePressBookmark(item)}>
                    {bookmarkMap?.[item.item.sno] ? (
                      <IconBookmarkFilled
                        size={16}
                        fill={Colors.content_pink_primary}
                      />
                    ) : (
                      <IconBookmark size={16} fill={Colors.content_tertiary} />
                    )}
                    <Typography type={'subtitle4'} color={'content_tertiary'}>
                      {bookmarkCount.value}
                      {bookmarkCount.label}
                    </Typography>
                  </Flex.Row>
                </Flex.Row>
              </Flex>
              {(item.item.contents ?? '').length > 0 && (
                <Flex width={160} padding={{ bottom: 8 }}>
                  <Typography
                    numberOfLines={1}
                    ellipsizeMode={'tail'}
                    type={'body4'}
                    color={'content_secondary'}
                  >
                    {item.item.contents}
                  </Typography>
                </Flex>
              )}
            </Flex>
          );
        }}
        viewabilityConfig={{
          itemVisiblePercentThreshold: 0.5,
        }}
        onViewableItemsChanged={logDisplayContent}
      />
      <VerticalSpacing size={16} />
    </View>
  );
};

export const RelatedOutfits: React.FC<RelatedOutfitsProps> = props => {
  return (
    <React.Suspense fallback={<></>}>
      <RelatedOutfitsContent {...props} />
    </React.Suspense>
  );
};
